/* ---------------------------------------------------------------------------
   SFD product page

   Behaviour for sections/sfd-product.liquid: the quantity stepper, the variant
   chips, Add to basket and Add to Quote.

   Kept in its own asset rather than inline in the section so a behaviour change
   is a small upload instead of rewriting the whole Liquid file. It needs no
   Liquid: everything it depends on arrives either as a data attribute on the
   section root or in the JSON script tag.

   Deliberately small. The price, the stock line and the delivery date are all
   server rendered, so there is no painting to do here: changing a variant
   refetches the section through the Section Rendering API and Liquid works the
   lot out again, including the variant level delivery date. That is the same
   approach the configurators take and it is the reason none of those three
   things can ever drift out of step with the chosen variant.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var root = document.querySelector('[data-pdp]');
  if (!root) return;

  var SECTION_ID = root.getAttribute('data-section-id');
  var PRODUCT_URL = root.getAttribute('data-product-url');
  var AFTER_ADD = root.getAttribute('data-after-add') || 'drawer';
  var QUOTE_URL = root.getAttribute('data-quote-url') || '';

  var busy = false;

  function state() {
    try {
      return JSON.parse(root.querySelector('[data-pdp-state]').textContent);
    } catch (e) {
      return {};
    }
  }

  /* ---------------- cart ----------------
     The theme's cart drawer is a custom element with onCartRefresh() and
     show(). Publishing cart-update as well keeps the header count and the
     free-delivery goal in step: the /cart/add.js response is not asked for
     sections and carries nothing we read.

     Copied from the configurators on purpose rather than reinvented. Every step
     is guarded and the function returns a boolean, because an add that
     succeeded must never surface as an error just because the drawer
     misbehaved. */
  function openCartDrawer() {
    try {
      if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
        window.FoxTheme.pubsub.publish('cart-update', { source: 'sfd-pdp' });
      }
    } catch (e) { /* the drawer is cosmetic, never block the add on it */ }
    var cd = document.querySelector('cart-drawer');
    if (!cd) return false;
    try {
      if (typeof cd.onCartRefresh === 'function') cd.onCartRefresh();
      if (typeof cd.show === 'function') { cd.show(); return true; }
    } catch (e) { return false; }
    return false;
  }

  function addToCart(id, qty, btn) {
    var errEl = root.querySelector('[data-pdp-err]');
    var label = btn.textContent;
    var buttons = root.querySelectorAll('[data-pdp-add]');
    buttons.forEach(function (b) { b.disabled = true; });
    btn.textContent = 'Adding…';
    if (errEl) errEl.hidden = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, body: j }; });
    }).then(function (res) {
      if (!res.ok) throw new Error((res.body && res.body.description) || 'Could not add that to the basket.');
      btn.textContent = label;
      buttons.forEach(function (b) { b.disabled = false; });
      if (AFTER_ADD === 'cart') { window.location.href = '/cart'; return; }
      if (AFTER_ADD === 'stay') {
        btn.textContent = 'Added';
        setTimeout(function () { btn.textContent = label; }, 1600);
        return;
      }
      var opened = openCartDrawer();
      if (!opened) {
        btn.textContent = 'Added';
        setTimeout(function () { btn.textContent = label; }, 1600);
      }
    }).catch(function (e) {
      if (errEl) { errEl.textContent = e.message; errEl.hidden = false; }
      btn.textContent = label;
      buttons.forEach(function (b) { b.disabled = false; });
    });
  }

  /* ---------------- quote basket ----------------
     quote-basket.js declares bare globals, so check before calling. price is
     PENCE: the drawer divides by 100 and the same array is posted to the quote
     backend, so a pounds figure here ships a wrong draft order.

     addToQuote renders the drawer, opens it and raises the toast itself, which
     is why there is nothing to do after it. Calling updateQuoteCount() as well
     would be harmless and wrong: saveQuote already does it. */
  function quoteReady() {
    return typeof window.addToQuote === 'function';
  }

  function addToQuote(qty) {
    var s = state();
    if (!s.variant) return;
    if (!quoteReady()) {
      if (QUOTE_URL) window.location.href = QUOTE_URL;
      return;
    }
    window.addToQuote(
      s.variant.id,
      qty,
      s.product.title,
      s.variant.title === 'Default Title' ? '' : s.variant.title,
      s.variant.price,
      s.product.img,
      s.product.url,
      null
    );
  }

  /* ---------------- changing variant ----------------
     Refetch this section for the chosen variant rather than repainting from
     JavaScript. Liquid then recomputes the price in both VAT modes, the stock
     line, the delivery date against custom.variant_delivery_days, and the
     JSON-LD, all from one request. Nothing can drift.

     root.innerHTML is replaced but root itself is not, so the delegated
     listener below survives and needs no rebinding. */
  function selectVariant(id) {
    if (busy) return;
    busy = true;
    root.classList.add('is-busy');

    var url = PRODUCT_URL + '?variant=' + encodeURIComponent(id) +
              '&section_id=' + encodeURIComponent(SECTION_ID);

    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not load that option.');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-pdp]');
        if (!fresh) throw new Error('Could not load that option.');
        root.innerHTML = fresh.innerHTML;
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', id);
          window.history.replaceState({}, '', u.toString());
        } catch (e) { /* a stale address bar is not worth failing the swap for */ }
      })
      .catch(function (e) {
        var err = root.querySelector('[data-pdp-err]');
        if (err) { err.textContent = e.message; err.hidden = false; }
      })
      /* acts as a finally: without it one failed refetch wedges the picker
         permanently, because selectVariant opens with if (busy) return. */
      .then(function () {
        busy = false;
        root.classList.remove('is-busy');
      });
  }

  /* ---------------- quantity ---------------- */
  function qtyValue() {
    var box = root.querySelector('[data-pdp-qty]');
    return Math.max(1, parseInt(box && box.value, 10) || 1);
  }

  /* ---------------- one delegated listener ----------------
     On the section root, so it keeps working after a variant swap replaces
     everything inside it. */
  root.addEventListener('click', function (e) {
    var step = e.target.closest('[data-pdp-step]');
    if (step) {
      var box = root.querySelector('[data-pdp-qty]');
      if (!box) return;
      var n = (parseInt(box.value, 10) || 1) + (parseInt(step.getAttribute('data-pdp-step'), 10) || 0);
      box.value = Math.max(1, n);
      return;
    }

    var chip = e.target.closest('[data-pdp-variant]');
    if (chip) {
      if (chip.getAttribute('aria-pressed') === 'true' || chip.disabled) return;
      selectVariant(chip.getAttribute('data-pdp-variant'));
      return;
    }

    var add = e.target.closest('[data-pdp-add]');
    if (add) {
      var s = state();
      if (!s.variant) return;
      addToCart(s.variant.id, qtyValue(), add);
      return;
    }

    var quote = e.target.closest('[data-pdp-quote]');
    if (quote) {
      addToQuote(qtyValue());
      return;
    }
  });

  root.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-pdp-select]');
    if (sel) selectVariant(sel.value);
  });
})();
